import { useState } from 'react';
import type { PrefetchResult } from './register';
import { useLang } from '../i18n/lang';

/**
 * "Save for offline" — asks the worker to pull the whole current build, and reports what it saved.
 *
 * The download itself is injected as `download`, so this component is the button and its states and
 * nothing else: a test drives it with a resolved (or rejected) promise, and App hands it the real
 * `requestOfflineDownload`. The status is a live region, so a reader using a screen reader hears the
 * result rather than only seeing the label change.
 */
type State =
  | { kind: 'idle' }
  | { kind: 'saving'; done: number; total: number }
  | { kind: 'saved'; saved: number; total: number }
  | { kind: 'error' };

export interface OfflineButtonProps {
  /**
   * Triggers the download and resolves when the worker has saved what it could.
   *
   * Takes the progress callback the worker feeds as it goes. The library is 34 files — 23.4 MB over
   * the wire, 90.7 MB in the cache — which is minutes on a phone, and for all of it this button
   * showed one unchanging label with no way to tell a slow download from a hung one.
   */
  download: (onProgress: (done: number, total: number) => void) => Promise<PrefetchResult>;
}

export function OfflineButton({ download }: OfflineButtonProps): React.JSX.Element {
  const { t } = useLang();
  const [state, setState] = useState<State>({ kind: 'idle' });
  const busy = state.kind === 'saving';

  async function run(): Promise<void> {
    setState({ kind: 'saving', done: 0, total: 0 });
    try {
      const result = await download((done, total) => { setState({ kind: 'saving', done, total }); });
      setState({ kind: 'saved', saved: result.saved, total: result.total });
    } catch {
      setState({ kind: 'error' });
    }
  }

  return (
    <div className="offline">
      <button
        type="button"
        className="offline-button"
        /* Size, hover and colour live in `.offline-button` now — see index.css. */
        onClick={() => void run()}
        disabled={busy}
      >
        {/* The count only once the worker has answered: "0 of 0" before the first file lands is
            worse than the plain word, and the first reply arrives with the first file. */}
        {!busy
          ? t('offline.save')
          : state.kind === 'saving' && state.total > 0
            ? t('offline.savingCount', { done: state.done, total: state.total })
            : t('offline.saving')}
      </button>
      <span role="status" aria-live="polite" className="offline-status">
        {state.kind === 'saved' && t('offline.saved', { saved: state.saved, total: state.total })}
        {state.kind === 'error' && t('offline.error')}
      </span>
    </div>
  );
}
